import { message, notification, Select } from "antd";
import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LogBookLoginRequired from "../../hooks/LogBookLoginRequired";
import { v4 as uuidv4 } from 'uuid';

export default function EmployeeEditEntry() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [visitorDetails, setVisitorDetails] = useState({
    id: "",
    visitorType: "",
    visitorId: "",
    visitorName: "",
    purposeOfVisit: "",
    phoneNumber: "",
    email: "",
    date: "",
    inTime: "",
    facility: "",
    outTime: "",
    authorizedSignatory: "",
    status: "",
    signatureFilePath: "",
  });

  const role = localStorage.getItem("role");

  const [editableFields, setEditableFields] = useState({
    outTime: "",
    authorizedSignatory: "",
    eSignature: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [signatureFilePath, setSignatureFilePath] = useState("");
  const [existingSignatureImage, setExistingSignatureImage] = useState(null);
  const [isOutTimeEditable, setIsOutTimeEditable] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  const urlRole = new URLSearchParams(window.location.search).get('role');
  const localRole = localStorage.getItem('role');

if (localStorage.getItem('isLogBookLoggedIn') !== 'true' || urlRole && urlRole !== localRole) {
  console.log(urlRole,"+++++",localRole)
  const fullPath=window.location.pathname + window.location.search;
  LogBookLoginRequired(fullPath)
}


  const fetchSignatureImage = useCallback(async (filePath) => {
    try {
      let fileName = filePath;

      if (fileName.startsWith("/")) {
        fileName = fileName.substring(1);
      }

      if (fileName.startsWith("signatures/")) {
        fileName = fileName.substring("signatures/".length);
      }

      console.log("Processing file path:", filePath, "-> fileName:", fileName);

      const response = await fetch(
        `https://techsolutionsservice.ustdev.com/api/logbook/signature/view/${encodeURIComponent(
          fileName
        )}`,
        {
          method: "GET",
          headers: {
            Accept: "image/*",
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        return blobUrl;
      } else {
        console.error(
          `Failed to fetch signature image: ${response.status} ${response.statusText}`
        );
        return null;
      }
    } catch (error) {
      console.error("Error fetching signature image:", error);
      return null;
    }
  }, []);

  // Function to load existing signature onto canvas
  const loadSignatureOnCanvas = useCallback((imageUrl) => {
    const canvas = document.getElementById("signatureCanvas");
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setSignatureData(canvas.toDataURL());
    };

    img.src = imageUrl;
  }, []);

  // Fetch visitor details
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://techsolutionsservice.ustdev.com/logbook/api/${id}`
        );
        if (response?.data?.data) {
          setVisitorDetails(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching visitor details:", error);
        navigate("/logbook/view-entries");
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, navigate]);

  // Process visitor details and set up editable fields
  useEffect(() => {
    if (!visitorDetails.id) return;

    // Set initial editable fields
    setEditableFields((prev) => ({
      ...prev,
      outTime: visitorDetails.outTime || "",
      authorizedSignatory: visitorDetails.authorizedSignatory || "",
    }));

    // Check if outTime should be editable
    const hasAuthorizedSignatory = visitorDetails.authorizedSignatory?.trim();
    const hasSignature = visitorDetails.signatureFilePath?.trim();
    const validStatus = ["APPROVED", "In Progress"].includes(
      visitorDetails.status
    );

    if (
      role === "ADMINUSER" &&
      hasAuthorizedSignatory &&
      hasSignature &&
      validStatus
    ) {
      setIsOutTimeEditable(true);
    }

    if (
      role === "SUPERUSER" &&
      visitorDetails.status == "APPROVED" &&
      visitorDetails.requestedBy == localStorage.getItem("Name")
    ) {
      setIsOutTimeEditable(true);
    }

    // Handle existing signature
    if (visitorDetails.signatureFilePath) {
      setSignatureFilePath(visitorDetails.signatureFilePath);

      fetchSignatureImage(visitorDetails.signatureFilePath).then((imageUrl) => {
        if (imageUrl) {
          setExistingSignatureImage(imageUrl);
          setTimeout(() => loadSignatureOnCanvas(imageUrl), 100);
        }
      });
    }
  }, [visitorDetails, role]);

  const isSignPresent=()=>{

    const canvas=document.getElementById("signatureCanvas");
    const context=canvas.getContext("2d");
    const pixelBuffer = new Uint32Array(
      context.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );
  
    // If any pixel is not transparent (not 0), return true
    return pixelBuffer.some(color => color !== 0);
  }

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (existingSignatureImage) {
        URL.revokeObjectURL(existingSignatureImage);
      }
    };
  }, [existingSignatureImage]);

  const isEmployee = visitorDetails.visitorType === "EMPLOYEE";
  const idForUrl = isEmployee ? visitorDetails.visitorId : visitorDetails.id;

  // Handle change for editable fields
  const handleChange = (e) => {
    const { name, value } = e.target;

    console.log(name+" "+value);
    
    setEditableFields((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle outTime change
  const handleOutTimeChange = (e) => {
    const { value } = e.target;
    const outTimeDate = new Date(`1970-01-01T${value}:00`);
    const inTimeDate = new Date(`1970-01-01T${visitorDetails.inTime}:00`);
    console.log( value);
    if(outTimeDate<=inTimeDate){
      console.log(outTimeDate+" "+inTimeDate);
      
      setErrors((prev) => ({
        ...prev,
        outTime: "out time must be after in time",
      }));
      return;
    }

    
    setVisitorDetails((prev) => ({
      ...prev,
      outTime: value,
    }));
    setEditableFields((prev) => ({
      ...prev,
      outTime: value,
    }));

    if (errors.outTime) {
      setErrors((prev) => ({ ...prev, outTime: "" }));
    }
    
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};

    if (role === "ADMINUSER" && !editableFields.outTime?.trim()) {
      newErrors.outTime = "Out Time is required";
    }

    if (!editableFields.authorizedSignatory?.trim()) {
      newErrors.authorizedSignatory = "Authorized Signatory is required";
    }

    if (role==="SUPERUSER" &&!signatureData) {
      newErrors.eSignature = "E-Signature is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveSignatureLocally = async () => {
    const canvas = document.getElementById("signatureCanvas");
    if (!canvas) throw new Error("Canvas not found");

    const dataURL = canvas.toDataURL("image/png");
    const blob = await (await fetch(dataURL)).blob();

    const formData = new FormData();
     const fileName = `${uuidv4()}.png`;
    formData.append("file", blob, fileName);
    console.log(formData);

    try {
      const uploadResponse = await fetch(
        "https://techsolutionsservice.ustdev.com/api/logbook/sign/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!uploadResponse.ok) throw new Error("Upload failed");

      const savedPath = `/signatures/${fileName}`;
      setSignatureFilePath(savedPath);
      return savedPath;
    } catch (err) {
      console.error("Signature upload error:", err);
      throw new Error("Failed to save signature");
    }
  };

  const updateLocalStorage = (visitorId, updatedFields) => {
    try {
      const savedEntries = JSON.parse(
        localStorage.getItem("visitorEntries") || "[]"
      );
      const updatedEntries = savedEntries.map((entry) => {
        if (
          entry.visitorId === visitorId ||
          entry.key === visitorId ||
          entry.id === visitorId
        ) {
          return { ...entry, ...updatedFields };
        }
        return entry;
      });
      localStorage.setItem("visitorEntries", JSON.stringify(updatedEntries));
    } catch (error) {
      console.error("Error updating localStorage:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    if (!isSignPresent()) {
      setErrors(prev => ({
        ...prev,
        eSignature: "E-Signature is required",
      }));
      return; 
    }

    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      const { visitorId } = visitorDetails;
      const outTime = editableFields.outTime || visitorDetails.outTime;

      if (
        role === "SUPERUSER" &&
        visitorDetails.requestedBy != localStorage.getItem("Name")
      ) {
        let path = signatureFilePath;

        if (!path) {
          path = await saveSignatureLocally();
        }

        const response = await fetch(
          `https://techsolutionsservice.ustdev.com/api/logbook/approve/${idForUrl}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              signatureFilePath: path,
              authorizedSignatory: editableFields.authorizedSignatory,
              visitorType: visitorDetails.visitorType,
            }),
          }
        );

        if (!response.ok) throw new Error("Approval failed");

        updateLocalStorage(visitorId, {
          authorizedSignatory: editableFields.authorizedSignatory,
          eSignature: path,
        });

        setSubmitMessage("Visitor approved successfully.");
      } else if (
        role === "ADMINUSER" ||
        (role === "SUPERUSER" &&
          visitorDetails.requestedBy == localStorage.getItem("Name"))
      ) {
        const response = await fetch(
          `https://techsolutionsservice.ustdev.com/api/logbook/update/outtime/${idForUrl}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              outTime: outTime,
              visitorType: visitorDetails.visitorType,
            }),
          }
        );

        if (!response.ok) throw new Error("Update failed");

        updateLocalStorage(visitorId, { outTime });
        setSubmitMessage("Out time updated successfully.");
      }

      setTimeout(() => {
        navigate("/logbook/view-entries");
      }, 1500);
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitMessage(`Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/logbook/view-entries");
  };

  const startDrawing = (e) => {
    if (role === "ADMINUSER") return;

    const canvas = e.target;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || role === "ADMINUSER") return;

    const canvas = e.target;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "black";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (isDrawing) {
      const canvas = e.target;
      setSignatureData(canvas.toDataURL());
      setIsDrawing(false);

      // Clear signature error if exists
      if (errors.eSignature) {
        setErrors((prev) => ({ ...prev, eSignature: "" }));
      }
    }
  };

  const clearSignature = () => {
    if (role === "ADMINUSER") return;

    const canvas = document.getElementById("signatureCanvas");
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  // Don't render if no visitor data
  if (!visitorDetails.id) {
    return (
      <div className="flex justify-center items-center h-64">Loading...</div>
    );
  }


  return (
    <div className="w-full max-w-screen-xl mx-auto p-6 pb-32">
      {/* Visitor Details Card */}
      <div className="mb-6 bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
          <h2 className="text-center font-semibold text-gray-800 text-lg tracking-wide">
            DETAILS
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Requested Raised By
                </label>
                <input
                  type="text"
                  value={
                    visitorDetails.onBehalfOf === "NA" ||
                    visitorDetails.onBehalfOf === null
                      ? "Me"
                      : "On Behalf Of"
                  }
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>

              {visitorDetails.onBehalfOf !== "NA" &&
                visitorDetails.onBehalfOf !== null && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      <span className="text-red-500">*</span>On Behalf Of Person
                      Name
                    </label>
                    <input
                      type="text"
                      value={visitorDetails.onBehalfOf}
                      disabled
                      className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                    />
                  </div>
                )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Visitor Type
                </label>
                <input
                  type="text"
                  value={visitorDetails.visitorType}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Visitor ID
                </label>
                <input
                  type="text"
                  value={visitorDetails.visitorId}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Visitor Name
                </label>
                <input
                  type="text"
                  value={visitorDetails.visitorName}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Phone Number
                </label>
                <input
                  type="tel"
                  value={visitorDetails.phoneNumber}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
             
              </div>
              {(visitorDetails.onBehalfOf === "NA" || visitorDetails.onBehalfOf === null ) &&  
              <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={visitorDetails.email}
                disabled
                className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
              />
            </div>}
            </div>

            <div className="space-y-6">
            {(visitorDetails.onBehalfOf !== "NA" &&
                visitorDetails.onBehalfOf !== null)&&(<div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={visitorDetails.email}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>)}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Purpose of Visit
                </label>
                <input
                  type="text"
                  value={visitorDetails.purposeOfVisit}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Facility
                </label>
                <input
                  type="text"
                  value={visitorDetails.facility}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Date
                </label>
                <input
                  type="date"
                  value={visitorDetails.date}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>In Time
                </label>
                <input
                  type="time"
                  value={visitorDetails.inTime}
                  disabled
                  className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md text-gray-600 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>Out Time
                </label>
                <input
                  type="time"
                  value={visitorDetails.outTime}
                  onChange={handleOutTimeChange}
                  disabled={!isOutTimeEditable}
                  className={`w-full p-3 border rounded-md transition-colors duration-200 ${
                    isOutTimeEditable
                      ? "bg-white text-gray-900 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                      : "bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed"
                  } ${
                    errors.outTime
                      ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                      : ""
                  }`}
                />
                {errors.outTime && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    {/* <span className="text-red-500">⚠</span> */}
                    {errors.outTime}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Entry Card */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
          <h2 className="text-center font-semibold text-gray-800 text-lg tracking-wide">
            AUTHORIZED ACCESS
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-x-8 gap-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                <span className="text-red-500">*</span>Authorized Signatory
              </label>
              <Select
                name="authorizedSignatory"
                value={editableFields.authorizedSignatory}
                onChange={(value) =>
                  handleChange({
                    target: { name: "authorizedSignatory", value: value },
                  })
                }
                disabled={role === "ADMINUSER" || visitorDetails.status==="CANCELLED" || visitorDetails.status==="COMPLETED"}
                className="w-full border rounded-md"
                status={errors.authorizedSignatory ? "error" : ""}
              >
                <Select.Option value={`${localStorage.getItem("Name")}`}>
                  {localStorage.getItem("Name")}
                </Select.Option>
              </Select>
              {errors.authorizedSignatory && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.authorizedSignatory}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                <span className="text-red-500">*</span>E-Signature
              </label>
              <div
                className={`border rounded-md p-4 ${
                  role === "ADMINUSER" ? "bg-gray-100" : "bg-white"
                } ${errors.eSignature ? "border-red-500" : "border-gray-300"}`}
              >
                <canvas
                  id="signatureCanvas"
                  width="350"
                  height="120"
                  className={`w-full border border-gray-200 rounded ${
                    role !== "ADMINUSER"
                      ? "cursor-crosshair bg-gray-50"
                      : "bg-gray-100 cursor-not-allowed"
                  }`}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onChange={handleChange}
                ></canvas>
                <button
                  type="button"
                  onClick={clearSignature}
                  disabled={role === "ADMINUSER" || visitorDetails.status==="CANCELLED" || visitorDetails.status==="COMPLETED"}
                  className="mt-3 px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Clear
                </button>
              </div>
              {errors.eSignature && (
                <p className="text-red-500 text-xs mt-1">{errors.eSignature}</p>
              )}
            </div>
          </div>

          {submitMessage && (
            <div
              className={`mt-6 p-4 rounded-md ${
                submitMessage.includes("Error")
                  ? "bg-red-100 text-red-700 border border-red-200"
                  : "bg-green-100 text-green-700 border border-green-200"
              }`}
            >
              {submitMessage}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-6 mt-8 mb-12 pb-2">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          hidden={visitorDetails.status==="CANCELLED" || visitorDetails.status==="COMPLETED"}
          className="bg-slate-700 text-white px-8 py-2.5 rounded-full hover:bg-slate-800 disabled:opacity-70 transition-colors font-medium"
        >
          {isSubmitting
            ? "Updating..."
            : role === "SUPERUSER"
            ? "Approve"
            : "Update"}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="bg-slate-700 text-white px-8 py-2.5 rounded-full hover:bg-slate-800 transition-colors font-medium"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
